import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SUPPORTED_SYMBOLS } from "@/lib/supported-symbols";
import { Bell, TrendingUp } from "lucide-react";

const ALL_SYMBOLS = Array.from(SUPPORTED_SYMBOLS);

const PriceAlertSettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(5);
  const [symbols, setSymbols] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.from("price_alert_preferences").select("*").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => {
        if (data) {
          setEnabled(data.enabled);
          setThreshold(Number(data.threshold_percent));
          setSymbols(data.symbols || []);
        }
      });
  }, [user]);

  const toggleSymbol = (s: string) => {
    setSymbols(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const save = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase.from("price_alert_preferences").upsert({
      user_id: user.id, enabled, threshold_percent: threshold, symbols,
      channels: ["in_app", "email"], updated_at: new Date().toISOString(),
    });
    setSaving(false);
    if (error) toast({ title: "فشل الحفظ", description: error.message, variant: "destructive" });
    else toast({ title: "تم الحفظ ✅" });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" /> تنبيهات أسعار العملات
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
          <div>
            <Label className="text-base">تفعيل التنبيهات</Label>
            <p className="text-xs text-muted-foreground">إشعار + بريد عند تغيّر السعر</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        <div>
          <Label>عتبة التغير (%)</Label>
          <Input type="number" min="1" max="50" step="0.5" value={threshold}
            onChange={e => setThreshold(Number(e.target.value))} className="mt-1" />
          <p className="text-xs text-muted-foreground mt-1">سيتم التنبيه عند تغير السعر بأكثر من هذه النسبة خلال ساعة</p>
        </div>

        <div>
          <Label className="mb-2 block">العملات المراقبة</Label>
          <div className="flex flex-wrap gap-2">
            {ALL_SYMBOLS.map(s => {
              const on = symbols.includes(s);
              return (
                <Badge key={s} variant={on ? "default" : "outline"}
                  className="cursor-pointer" onClick={() => toggleSymbol(s)}>
                  {s}
                </Badge>
              );
            })}
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="w-full gap-2">
          <Bell className="w-4 h-4" /> {saving ? "جاري الحفظ..." : "حفظ الإعدادات"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default PriceAlertSettings;
